/**
 * Display copy (title + subtitle) for each submission status shown in the
 * status banner and the page title on the submission status screen.
 *
 * @module submissionStatusCopy
 */

import { SubmissionStatusEnum } from "../../../data-model/submissionDocs";

/** Title and supporting subtitle text for a given submission status. */
export interface SubmissionStatusCopy {
  title: string;
  subtitle: string;
}

/** `workflow_config.submission_type` value for fax-based submissions. */
const FAX_SUBMISSION_TYPE = "fax";

const FAX_SUBMITTED_COPY: SubmissionStatusCopy = {
  title: "Fax Submitted",
  subtitle:
    "The fax has been queued to the payer. Awaiting a delivery confirmation.",
};

/**
 * Resolves the banner/page copy for a submission status.
 *
 * @input status - Current submission status of the order. Null/empty means no
 *   filing attempt has been recorded yet.
 * @input submissionType - `workflow_config.submission_type` for the order. On a
 *   fax workflow an `in_progress` status means the fax has been queued, so the
 *   "Fax Submitted" copy is shown instead of the portal-automation copy.
 * @returns The title and subtitle to display for the status.
 */
export const getSubmissionStatusCopy = (
  status?: SubmissionStatusEnum | "",
  submissionType?: string,
): SubmissionStatusCopy => {
  // Null/empty Postgres `submission_status` means no filing attempt yet — not
  // an in-progress automation run.
  if (!status) {
    return {
      title: "Not Submitted",
      subtitle:
        "No filing attempt has been recorded for this order yet. Submit via automation, or mark as submitted after filing on the payer portal.",
    };
  }

  switch (status) {
    case "in_progress":
      return submissionType === FAX_SUBMISSION_TYPE
        ? FAX_SUBMITTED_COPY
        : {
            title: "Submission In Progress",
            subtitle:
              "The bot is currently filling the form on the payer portal. You can continue working on other cases.",
          };
    case "fax_submitted":
      return FAX_SUBMITTED_COPY;
    case "completed":
      return {
        title: "Submitted Successfully",
        subtitle:
          "The bot finished filing on the payer portal. Confirm on the portal, then mark as submitted.",
      };
    case "submission_completed":
      return {
        title: "Marked as Submitted by User",
        subtitle:
          "Authorization has been confirmed by the user on the payer portal.",
      };
    case "saved_draft":
      return {
        title: "Draft Saved - Action Required",
        subtitle:
          "The automation completed part of the submission before encountering an issue. Continue on the portal to complete the remaining steps.",
      };
    case "fax_delivered":
      return {
        title: "Fax Delivered",
        subtitle:
          "The fax was delivered to the payer. A confirmation was captured.",
      };
    case "fax_error":
    case "fax_failed":
      return {
        title: "Fax Not Delivered",
        subtitle:
          "The fax could not be delivered. Please retry or submit manually on the payer portal.",
      };
    case "already_filed":
      return {
        title: "Auth Already on File",
        subtitle: "Authorization is already on file for this order.",
      };
    case "payer_not_supported":
      return {
        title: "Payer Not Supported",
        subtitle:
          "Automated submission is not supported for this payer. Please submit manually on the payer portal.",
      };
    case "error":
      return {
        title: "Submission Failed",
        subtitle:
          "The filing attempt encountered an error. Review the details below and retry.",
      };
    default:
      return {
        title: "Submission In Progress",
        subtitle:
          "The bot is currently filling the form on the payer portal. You can continue working on other cases.",
      };
  }
};
